import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Chats, Filters, Commands } from '@local/client-contracts';
import { KeyName } from '@local/ts-infra';
import { PopupRef } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { isRightClick } from '@shared/utils';
import { isEmpty } from 'lodash';
import { ResultContextMenuItem } from '../../../results/models/results-types';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { BaseFile } from 'src/app/bar/services/blobs/blobs-storage.service';
import { ChatFileService } from '../../services/chat-files.service';
import { filter, Subscription } from 'rxjs';
import { isQuoteMessage, isExtension } from '@local/common-web';
import { WritingAssistantService } from 'src/app/bar/services/writing-assistant.service';

@UntilDestroy()
@Component({
  selector: 'chat-question-message',
  templateUrl: './chat-question-message.component.html',
  styleUrls: ['./chat-question-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatQuestionMessagesComponent {
  private readonly CONTEXT_MENU_OPEN_AT = 40;
  private filesSub: Subscription;
  private isExtension = isExtension();

  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  contextMenuActive: boolean;
  files = signal<BaseFile[]>([]);
  filtersToolTip = signal<string>('');
  actionMessage = signal<{ text?: string; icon?: string }>({});
  isExpanded = signal<boolean>(false);
  isOverflowed = signal<boolean>(false);

  message = input<Chats.UserMessage>();

  quoteTextRef = viewChild<ElementRef>('quoteText');

  isQuoteMessage = computed(() => !isEmpty(this.message().action) && isQuoteMessage(this.message().action));

  @HostListener('auxclick', ['$event']) onAuxClick(event: MouseEvent) {
    if (isRightClick(event)) {
      const selectedText = window.getSelection().toString();
      this.openContextMenu(event, selectedText);
    }
  }

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private contextMenuService: ContextMenuService,
    private commandsService: CommandsService,
    private chatFileService: ChatFileService,
    private writingAssistantService: WritingAssistantService
  ) {
    effect(
      () => {
        const message = this.message();
        if (this.message()) {
          if (!isEmpty(message.filters || {})) {
            this.filtersToolTip.set(this.createFiltersTooltip(message.filters));
          }
          this.initFiles();
        }
        if (this.isQuoteMessage()) {
          this.initActionMessage();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewInit() {
    const element = this.quoteTextRef()?.nativeElement;
    if (element) {
      this.isOverflowed.set(element.scrollHeight > 120);
    }
  }

  ngOnDestroy() {
    if (this.filesSub) {
      this.filesSub.unsubscribe();
    }
  }

  private async initActionMessage() {
    const viewAction = this.writingAssistantService.getViewAction(
      this.message().action.type,
      (this.message().action as Chats.TranslateMessage)?.language
    );
    this.actionMessage.set(viewAction);
  }

  private initFiles() {
    if (this.filesSub) {
      this.filesSub.unsubscribe();
    }
    this.filesSub = this.chatFileService
      .getFilesByBlobIds$(this.message()?.blobIds || [])
      .pipe(
        untilDestroyed(this),
        filter((f) => !!f)
      )
      .subscribe((files) => {
        this.files.set(files);
      });
  }

  private createFiltersTooltip(values: Filters.Values): string {
    return Object.entries(values)
      .map(([key, array]) => `${key}: ${array.join(', ')}`)
      .join(', ');
  }

  private buildContextMenuItems(selectedText: string): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];
    if (selectedText) {
      return [
        {
          id: 'copy',
          text: 'Copy',
          command: { type: 'copy-clipboard', value: selectedText } as Commands.CopyClipboard,
          shortcut: ['CommandOrControl', 'c'] as KeyName[],
          icon: { type: 'font-icon', value: 'icon-copy' },
        },
      ];
    }
    return items;
  }

  openContextMenu(event: MouseEvent, selectedText: string) {
    this.contextMenuActive = true;
    const items = this.buildContextMenuItems(selectedText);
    const openAt: { x: number; y: number } = {
      x: event.x - this.CONTEXT_MENU_OPEN_AT,
      y: event?.y,
    };
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ResultContextMenuItem) => this.handleContextMenuCommand(item),
      },
      { position: 'below' }
    );
    this.activeContextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuActive = false;
      this.cdr.markForCheck();
    });
  }

  private async handleContextMenuCommand(command: ResultContextMenuItem) {
    switch (command.id) {
      case 'copy': {
        await this.commandsService.executeCommand(command.command);
        break;
      }
    }
  }
}
